import { FlexColumn, FlexRow, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { StyledDivider } from 'baseui/divider';
import { HeadingSmall, LabelMedium, ParagraphMedium } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK } from '../../../constants/app';
import { useAuth } from '../../../hooks/useAuth';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { getAccount, updateEvent, updateEventsInCategory } from '../../../services/accountService';
import { accountState } from '../../../storage/account';
import {
  editingEventSettingState,
  eventSettingsInCategoryCountState,
  eventSettingState,
  selectedEventSettingState,
} from '../../../storage/eventSettings';
import { InternalEventSetting } from '../../../types/eventSetting';
import ConfirmModal from '../../shared/ConfirmModal';
import { DaysPicker } from '../../ui/DaysPicker';
import { StyledTooltip } from '../../ui/StyledTooltip';
import { TimeByDayPicker } from '../../ui/TimeByDayPicker';

export function EventDetail() {
  const { theme, css, classes } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const [editingEvent, setEditingEvent] = useRecoilState(editingEventSettingState);
  const resetEditingEvent = useResetRecoilState(editingEventSettingState);
  const selectedEvent = useRecoilValue(selectedEventSettingState);
  const event = useRecoilValue(eventSettingState(selectedEvent || ''));
  const category = event?.category;
  const eventsInCategoryCount = useRecoilValue(eventSettingsInCategoryCountState(category || ''));
  const resetSelectedEventId = useResetRecoilState(selectedEventSettingState);
  const setAccount = useSetRecoilState(accountState);
  const [enabledDays, setEnabledDays] = useState<Array<0 | 1 | 2 | 3 | 4 | 5 | 6>>();
  const { logout } = useAuth();
  const { openModal, closeModal, showModal } = useModal();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    resetEditingEvent();
    return () => {
      resetEditingEvent();
    };
  }, [selectedEvent]);

  useEffect(() => {
    if (!!event?.days.length) {
      setEnabledDays(event.days.map((day) => day.day));
    } else {
      setEnabledDays([]);
    }
  }, [selectedEvent]);

  if (editingEvent === undefined) return null;

  const hasUpdates = JSON.stringify(event) !== JSON.stringify(editingEvent);

  const onInputChange = (value: string | boolean | number | null | unknown, field: keyof InternalEventSetting) => {
    setEditingEvent((prev) => {
      return { ...prev!, [field]: value };
    });
  };

  const canSubmit = () => {
    if (!!editingEvent.days.length) {
      const error = editingEvent.days.filter((event) => {
        if (!event.allDay) {
          if (!event.startHour) return true;
          if (!event.endHour) return true;
          if (event.startHour < 8) return true;
          if (event.endHour <= event.startHour) return true;
        }
      });
      if (!!error.length) return false;
    }
    return true;
  };

  const onSaveEvent = async () => {
    startLoading();
    const { category, categoryDisplay, notificationDescription, internalId, ...eventData } = editingEvent;
    const { error } = await updateEvent(eventData);
    if (!error) {
      showPositiveFeedback(FEEDBACK.updated('El evento'));
      const { account, error } = await getAccount();
      if (!error && account) {
        setAccount(account);
        resetEditingEvent();
      } else {
        logout();
      }
    } else {
      showFailFeedback(error || 'Ocurrió un error al editar la notificación, inténtalo más tarde.');
    }
    stopLoading();
  };

  const onSaveForCategory = async () => {
    if (!category) return;
    const { error } = await updateEventsInCategory({
      push: editingEvent.push,
      playSound: editingEvent.playSound,
      days: editingEvent.days,
      category,
    });
    if (!error) {
      showPositiveFeedback(`Se actualizaron los eventos en "${editingEvent.categoryDisplay}" correctamente.`);
      const { account, error } = await getAccount();
      if (!error && account) {
        setAccount(account);
      } else {
        logout();
      }
    } else {
      showFailFeedback(error || 'Ocurrió un error al editar la notificación, inténtalo más tarde.');
    }
    closeModal();
  };

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        overflow: 'hidden',
        [theme.mediaQuery.large]: {
          maxWidth: '60%',
        },
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '50%',
        },
      })}
    >
      <FlexColumn gap={0}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredBetween}
        >
          <HeadingSmall margin={0}>{event?.notificationDescription}</HeadingSmall>
          {eventsInCategoryCount && eventsInCategoryCount > 1 && (
            <StyledTooltip
              showArrow={false}
              content={hasUpdates ? 'Debes guardar los cambios primero' : undefined}
            >
              <StyledButton
                disabled={hasUpdates}
                size="compact"
                onClick={openModal}
              >
                Aplicar a todos
              </StyledButton>
            </StyledTooltip>
          )}
        </FlexRow>
        <StyledDivider style={{ width: '100%', borderWidth: '2px' }} />
      </FlexColumn>
      <FlexColumn
        gap={theme.sizing.scale700}
        classNames={css({ flex: 1, overflow: 'auto' })}
      >
        <FlexColumn gap={theme.sizing.scale300}>
          <LabelMedium>Canales</LabelMedium>
          <Checkbox
            checked={editingEvent?.push}
            checkmarkType="toggle"
            onChange={(e) => onInputChange(e.target.checked, 'push')}
            labelPlacement="right"
          >
            Recibir notificaciones push
          </Checkbox>
          <Checkbox
            checked={editingEvent?.playSound}
            checkmarkType="toggle"
            onChange={(e) => onInputChange(e.target.checked, 'playSound')}
            labelPlacement="right"
          >
            Habilitar sonido
          </Checkbox>
        </FlexColumn>
        <FlexColumn gap={theme.sizing.scale300}>
          <LabelMedium>Días activos</LabelMedium>
          <DaysPicker
            enabledDays={enabledDays}
            onChange={(eventDays) => {
              const d: unknown = eventDays
                .map((day, index: unknown) => {
                  if (!day) return undefined;
                  return {
                    ...(editingEvent.days?.find((d) => {
                      return d.day === index;
                    }) || {
                      day: index as 0 | 1 | 2 | 3 | 4 | 5 | 6,
                      endHour: undefined,
                      startHour: undefined,
                      allDay: true,
                    }),
                  };
                })
                .filter((notUndefined) => notUndefined !== undefined);
              onInputChange(d, 'days');
            }}
          />
        </FlexColumn>
        {!!editingEvent?.days.length && (
          <FlexColumn gap={theme.sizing.scale300}>
            <LabelMedium>Horario</LabelMedium>
            <TimeByDayPicker
              days={editingEvent.days}
              onChange={(days) => onInputChange(days, 'days')}
            />
          </FlexColumn>
        )}
      </FlexColumn>
      <FlexRow
        classNames={classes.centeredEnd}
        gap={theme.sizing.scale300}
      >
        <StyledButton
          kind="tertiary"
          onClick={resetSelectedEventId}
        >
          Cancelar
        </StyledButton>
        <StyledButton
          onClick={onSaveEvent}
          isLoading={loading}
          disabled={!hasUpdates || !canSubmit()}
        >
          Guardar
        </StyledButton>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onSaveForCategory}
        confirmText="Aceptar"
        title="¡Atención!"
        description={
          <ParagraphMedium margin={0}>
            ¿Desea aplicar esta configuración a <b>todos los eventos</b> en la categoría{' '}
            <i>
              <b>"{editingEvent.categoryDisplay}"</b>
            </i>
            ?
          </ParagraphMedium>
        }
      />
    </FlexColumn>
  );
}
